import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Box } from '@mui/system';
import { KpiCategory } from 'benchmarking-domain/kpi-definition';
import {
  KpisToSectionsAssignments,
  KpiStructureDependencyFinder,
} from 'benchmarking-domain/kpi-structure-dependency-finder';
import Project from 'benchmarking-domain/project';
import ProjectSection from 'benchmarking-domain/project-section';
import { groupBy } from 'helpers/array';
import useApi from 'hooks/use-api';

import { KpiChartItem } from './kpi-chart-item';
import KpiDetails from './kpi-details';
import { useKpiStructure } from './use-kpi-structure';

export interface KpiViewerForSectionProps {
  project: Project;
  projectSections: ProjectSection[] | undefined;
  sectionId: string;
}

const KpiGroupAccordion: React.FC<React.PropsWithChildren & { group: string }> = ({ children, group }) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  return (
    <Accordion onChange={(_, ex) => setExpanded(ex)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
          <Typography variant="body1">KPIs for: {group}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingY: 2 }}>{expanded && children}</AccordionDetails>
    </Accordion>
  );
};

const KpiViewerForSection: React.FC<KpiViewerForSectionProps> = ({ project, projectSections, sectionId }) => {
  const { kpiBenchmarks, kpiStatistics, kpiStructure, scopeKpiValues, kpiHistoryItems, initLoading } = useKpiStructure(
    project,
    projectSections,
    sectionId
  );

  const [assignmentsToSections, setAssignmentsToSections] = React.useState<KpisToSectionsAssignments>({});

  const { subscribeKpisToSectionsAssignments } = useApi();

  React.useEffect(() => {
    const unsubscribeAssignments = subscribeKpisToSectionsAssignments(project.frameworkId, (data) => {
      const resolvedAssignments = KpiStructureDependencyFinder.getDynamicKpisToSectionsAssignmentsResolved(
        data,
        projectSections
      );
      setAssignmentsToSections(resolvedAssignments);
    });

    return () => {
      unsubscribeAssignments();
    };
  }, [project.frameworkId, setAssignmentsToSections, subscribeKpisToSectionsAssignments, projectSections]);

  const kpiDefinitions = React.useMemo(() => {
    const getAllKpisFromCategories = (categories: KpiCategory[]) => {
      if (!categories || categories.length === 0) return [];

      const allKpis = categories
        .map((c) => c.kpiDefinitions)
        .flat()
        .filter((kpi) => kpi !== undefined);

      allKpis.push(
        ...getAllKpisFromCategories(
          categories
            .map((c) => c.subCategories)
            .flat()
            .filter((c) => c !== undefined)
        )
      );

      return allKpis;
    };

    const allKpis = getAllKpisFromCategories(kpiStructure?.categories ?? []);

    const sectionKpis = assignmentsToSections[sectionId] ?? [];

    const filltered = allKpis.filter((kpi) =>
      sectionKpis.includes(kpi.dynamicKpiParentName ? `${kpi.id}@${kpi.dynamicKpiParentName}` : kpi.id)
    );

    return groupBy(filltered, 'dynamicKpiParentName');
  }, [sectionId, kpiStructure, assignmentsToSections]);

  if (initLoading) {
    return (
      <Stack spacing={2} paddingTop={5}>
        <Typography textAlign={'center'}>Loading data...</Typography>
      </Stack>
    );
  }

  if (Object.keys(kpiDefinitions).length === 0) {
    return (
      <Stack spacing={2} paddingTop={2}>
        <Alert severity="info">
          <Typography>No KPIs found for this section</Typography>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} paddingTop={2}>
      <Alert severity="info">
        <Typography>
          Note: KPIs are not final, and are pending completion of data input and review by Benchmarking Portal team
        </Typography>
      </Alert>
      <Box>
        {Object.entries(kpiDefinitions).map(([group, kpis]) => {
          const children = (
            <Grid container spacing={2} key={group}>
              {kpis.map((kpi) => (
                <Grid item key={`kpi-${kpi.dynamicKpiParentName}-${kpi.id}`} xs={12} md={6} xl={4}>
                  <KpiChartItem>
                    <KpiDetails
                      project={project}
                      kpi={kpi}
                      benchmarks={kpiBenchmarks}
                      statistics={kpiStatistics}
                      scopeKpiValues={scopeKpiValues}
                      historyItems={kpiHistoryItems}
                    />
                  </KpiChartItem>
                </Grid>
              ))}
            </Grid>
          );

          return group != '_' ? (
            <KpiGroupAccordion key={group} group={group} children={children} />
          ) : (
            <Box key={group} children={children} sx={{ marginBottom: 2 }} />
          );
        })}
      </Box>
    </Stack>
  );
};

export default KpiViewerForSection;
